import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExpenseRow, FilePreview } from '../models/ModelExpenses';

// Define a type for the raw file preview from the backend
interface RawFilePreview {
  statement_date: string;
  bank_name: string;
  preview_data: ExpenseRow[];
}

export function useExpensesPreview() {
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const navigate = useNavigate();
  const tableRef = useRef<HTMLDivElement>(null);

  const fetchPreviewData = useCallback(async () => {
    try {
      const response = await fetch('/api/expenses/preview/', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to fetch preview data');
      }

      let previewsToProcess: RawFilePreview[] = [];
      
      // Handle different response formats from backend
      if (data.multiple_tables === true && data.preview_tables) {
        // Multiple tables format
        previewsToProcess = data.preview_tables;
      } else if (data.multiple_tables === false) {
        // Single table format - wrap in array
        previewsToProcess = [{
          preview_data: data.data,
          bank_name: data.bank_name,
          statement_date: data.statement_date
        }];
      } else {
        // Fallback to old format if present
        previewsToProcess = data.previews || [];
      }

      const previewsFromBackend: FilePreview[] = previewsToProcess.map((filePreview: RawFilePreview) => {
        const processedRows = (filePreview.preview_data || []).map((row: ExpenseRow) => {
          let transactionType = row.type || 'variable expense';
          if (row.description && row.description.toLowerCase().includes('parcela')) {
            transactionType = 'installment expense';
          }
          return {
            ...row,
            type: transactionType
          };
        }).map((row: ExpenseRow) => {
          const classificationMapping = data.classification_mapping || {};
          if (row.description && classificationMapping[row.description]) {
            const mapped = classificationMapping[row.description];
            return {
              ...row,
              category: mapped.category,
              type: mapped.type
            };
          }
          return row;
        });
        return {
          statement_date: filePreview.statement_date,
          bank_name: filePreview.bank_name,
          preview_data: processedRows
        };
      });

      setPreviews(previewsFromBackend);
      setAvailableCategories(data.available_categories || []);
    } catch (error) {
      console.error('Error fetching preview data:', error);
      alert('Failed to load preview data. Redirecting back to import page.');
      navigate('/expenses/import');
    }
  }, [navigate]);

  useEffect(() => {
    fetchPreviewData();
  }, [fetchPreviewData]);

  const updateClassificationMapping = async (desc: string, mapping: { category: string; type: string }) => {
    try {
      await fetch('/api/expenses/save_classification_mapping/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description: desc,
          category: mapping.category,
          type: mapping.type,
        }),
      });
    } catch (error) {
      console.error("Error saving classification mapping", error);
    }
  };

  const handleCellChange = (previewIndex: number, rowIndex: number, field: keyof ExpenseRow, value: string) => {
    const previewsCopy = [...previews];
    const filePreview = previewsCopy[previewIndex];
    const updatedRows = [...filePreview.preview_data];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [field]:
        field === 'value'
          ? parseFloat(value) || 0
          : value,
    };
    filePreview.preview_data = updatedRows;
    previewsCopy[previewIndex] = filePreview;
    setPreviews(previewsCopy);
    
    const row = updatedRows[rowIndex];
    if (row.description && (field === 'category' || field === 'type')) {
      const newMappingEntry = {
         category: field === 'category' ? value : row.category,
         type: field === 'type' ? value : row.type,
      };
      updateClassificationMapping(row.description, newMappingEntry);
    }
  };

  const calculateTotal = (previewIndex: number): number => {
    if (previews[previewIndex]) {
      return previews[previewIndex].preview_data.reduce((sum, row) => sum + (row.value || 0), 0);
    }
    return 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Map each file preview to include its bank name, statement date, and the formatted expense rows
      const formattedPreviews = previews.map((filePreview: FilePreview) => ({
        statement_date: filePreview.statement_date,
        bank_name: filePreview.bank_name,
        preview_data: filePreview.preview_data.map((row: ExpenseRow) => ({
          date: row.date,
          description: row.description,
          value: row.value,
          category: row.category,
          type: row.type || 'variable expense',
        })),
      }));

      const response = await fetch('/api/expenses/confirm_import/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ previews: formattedPreviews }), // Sending array of previews
      });
      
      if (response.ok) {
        navigate('/expenses/dashboard');
      } else {
        const errorData = await response.json();
        alert(`Error confirming import: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error submitting data:', error);
      alert('Error confirming import');
    }
  };

  const formatNumber = (value: string | number): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn("Invalid date:", dateString);
        return '';
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return '';
    }
  };

  const getExpenseTypes = () => [
    'variable expense',
    'fixed expense',
    'subscription expense',
    'installment expense',
    'value_not_matched',
  ];

  return {
    previews,
    availableCategories,
    tableRef,
    calculateTotal,
    handleCellChange,
    handleSubmit,
    formatNumber,
    formatDateForInput,
    getExpenseTypes
  };
}